import { useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableImageProps {
  url: string;
  index: number;
  onRemove: (index: number) => void;
}

const SortableImage = ({ url, index, onRemove }: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: url + "-" + index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-xl overflow-hidden border-2 border-gray-200 hover:border-[#004A99] transition-all cursor-grab active:cursor-grabbing ${
        isDragging ? "opacity-80 shadow-xl" : ""
      }`}
      {...attributes}
      {...listeners}
    >
      <img
        src={url}
        alt={`Trang ${index + 1}`}
        className="w-full h-32 object-cover"
        loading="lazy"
      />
      {/* Page number badge */}
      <div className="absolute top-2 left-2 bg-[#004A99] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
        {index + 1}
      </div>
      {/* Remove button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-white/90 hover:bg-red-50 text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        title="Xóa"
      >
        <i className="ri-close-line text-xs" />
      </button>
      {/* Drag hint */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] text-center py-1 opacity-0 group-hover:opacity-100 transition-opacity">
        Kéo để sắp xếp
      </div>
    </div>
  );
};

interface FlipbookImageSorterProps {
  images: string[];
  onChange: (images: string[]) => void;
}

export const FlipbookImageSorter = ({ images, onChange }: FlipbookImageSorterProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((_, i) => active.id === images[i] + "-" + i);
      const newIndex = images.findIndex((_, i) => over.id === images[i] + "-" + i);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(images, oldIndex, newIndex));
      }
    }
  }, [images, onChange]);

  const handleRemove = useCallback((index: number) => {
    onChange(images.filter((_, i) => i !== index));
  }, [images, onChange]);

  if (images.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-600">
          Thứ tự trang ({images.length} ảnh)
        </p>
        <p className="text-[10px] text-gray-400">
          Kéo thả để sắp xếp lại thứ tự
        </p>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={images.map((url, i) => url + "-" + i)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {images.map((url, i) => (
              <SortableImage
                key={url + "-" + i}
                url={url}
                index={i}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default FlipbookImageSorter;